import React, { useState, useEffect, useMemo, useRef } from "react";
import { Pie, Column } from "@ant-design/plots";
import jsPDF from "jspdf";
import { RiskAssessment, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { LoadingSpinner, ExportPdfButton, KPICard, YearBar, ChartBox, ChartLegend } from "../../../Shared";
import { renderDonutForPdf, renderColumnForPdf, buildStandardDashboardPdf, captureElementAtWidth, prepareKpiCardsForPdf } from "../../../Shared/pdfCharts";
import { useLanguage } from "../../../../contexts";
import { useSite } from "../../../../contexts";
import { useCompany } from "../../../../contexts/CompanyContext";
import { CHART_PALETTE as PALETTE } from "../../../../utils/chartTheme";
import "./RiskAssessmentsDashboard.scss";

const riskCtrl = new RiskAssessment();
const siteCtrl = new Site();

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const y = CURRENT_YEAR - 2 + i;
  return { key: y, value: y, text: String(y) };
});

function deriveRiskYear(item) {
  if (item.year !== undefined && item.year !== null) return item.year;
  const dateStr = item.evaluationDate || item.createdAt;
  return dateStr ? new Date(dateStr).getFullYear() : null;
}

// Mismos 4 colores que venimos usando en las demás donas del sistema
// (DONUT_PALETTE) — antes eran rojo/naranja/amarillo/verde oscuro.
const RISK_COLORS = {
  CRITICO:  "#001936",
  ALTO:     "#8CCAFF",
  MODERADO: "#A3B76B",
  BAJO:     "#C4D693",
};
const RISK_ORDER = ["CRITICO", "ALTO", "MODERADO", "BAJO"];

// Las donas de nivel de riesgo usan RISK_COLORS (semántico, no esta
// paleta) — GREEN_PALETTE es solo para los gráficos de barra.
const GREEN_PALETTE = ["#7B9355", "#C4D693", "#A3B76B"];
const BAR_STYLE = { radius: 5, maxWidth: 25, shadowColor: "transparent", shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0 };
const BAR_SCALE_X = { padding: 0.08, paddingInner: 0.08, paddingOuter: 0.08 };
const BAR_Y_AXIS = {
  labelFontFamily: "Archivo",
  labelFontWeight: 800,
  labelFontSize: 8,
  labelLineHeight: 8,
  labelFill: "#001936",
  labelOpacity: 1,
  gridStroke: "#D3D3D3",
  gridStrokeOpacity: 1,
  gridLineWidth: 1,
  gridLineDash: [5, 6],
};
const BAR_X_AXIS = { labelFill: "#001936", labelOpacity: 1, labelAutoRotate: true, labelFontSize: 9 };
const BAR_INTERACTION = { tooltip: { crosshairs: false }, elementHighlightByX: false };

const HAZARD_LABELS = {
  machinery_equipment:            "Máquina / Equipo",
  hand_electric_tool:             "Herramienta manual",
  electrical_installation:        "Inst. eléctrica",
  hazardous_chemical:             "Sust. química",
  physical_agent:                 "Agente físico",
  biological_agent:               "Agente biológico",
  physical_workload:              "Carga física",
  work_organization_psychosocial: "Org. del trabajo",
  order_cleanliness:              "Orden y limpieza",
  height_work:                    "Trabajos en altura",
  vehicles_forklifts:             "Vehículos / Montacargas",
  confined_space:                 "Espacio confinado",
  construction_work:              "Obra / Construcción",
  other:                          "Otro",
};

const RISK_TYPE_LABELS = {
  mechanical:                      "Mecánico",
  electrical:                      "Eléctrico",
  ergonomic:                       "Ergonómico",
  chemical:                        "Químico",
  physical_noise:                  "Físico - Ruido",
  physical_vibration:              "Físico - Vibración",
  physical_extreme_temperature:    "Físico - Temperatura",
  physical_insufficient_lighting:  "Físico - Iluminación",
  physical_radiation:              "Físico - Radiación",
  biological:                      "Biológico",
  psychosocial_work_stress:        "Psicosocial",
  fire_explosion:                  "Incendio / Explosión",
  falling_objects:                 "Caída de objetos",
  same_level_fall:                 "Caída mismo nivel",
  different_level_fall:            "Caída diferente nivel",
  locative:                        "Locativo",
  other:                           "Otro",
};

// Algunas fuentes de peligro vienen guardadas con el detalle entre paréntesis
// (ej. "Agente físico (ruido, vibración, temperatura, iluminación)") — en el
// eje del gráfico mostramos solo el nombre corto del catálogo si matchea.
function resolveHazardLabel(raw) {
  if (HAZARD_LABELS[raw]) return HAZARD_LABELS[raw];
  const match = Object.values(HAZARD_LABELS).find((label) => raw.startsWith(label));
  if (match) return match;
  const paren = raw.indexOf("(");
  return paren > 0 ? raw.slice(0, paren).trim() : raw;
}

function EmptyMsg({ text }) {
  return <div className="rad__empty">{text || "Sin datos"}</div>;
}

function makePie(data, colorFn) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const colors = data.map((d, i) => colorFn ? colorFn(d.type) : PALETTE[i % PALETTE.length]);
  return {
    data,
    angleField: "value",
    colorField: "type",
    innerRadius: 0.6,
    scale: { color: { range: colors } },
    label: false,
    legend: false,
    height: 220,
    tooltip: {
      items: [{
        field: "value",
        name: "Cantidad",
        valueFormatter: (v) => `${v} (${total > 0 ? ((v / total) * 100).toFixed(1) : "0.0"}%)`,
      }],
    },
  };
}

export function RiskAssessmentsDashboard({ companyName, siteName } = {}) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { site: contextSite } = useSite();
  const { company } = useCompany();

  const [risks,     setRisks]     = useState(null);
  const [sites,     setSites]     = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const kpiRef = useRef();
  const [exportingPdf, setExportingPdf] = useState(false);
  const exportingRef = useRef(false);

  useEffect(() => {
    if (!company || company === "0") { setSites([]); return; }
    siteCtrl.getSitesByCompany(accessToken, company, true)
      .then((r) => setSites(Array.isArray(r) ? r : []))
      .catch(() => setSites([]));
  }, [company, accessToken]);

  useEffect(() => {
    const params = { active: true };
    if (contextSite && contextSite !== "0") params.site = contextSite;
    else if (company && company !== "0") params.company = company;
    riskCtrl.getRiskAssessments(accessToken, params)
      .then((r) => setRisks(Array.isArray(r) ? r : []))
      .catch(() => setRisks([]));
  }, [accessToken, contextSite, company]);

  const filteredRisks = useMemo(() => {
    if (!risks) return [];
    return risks.filter((r) => deriveRiskYear(r) === year);
  }, [risks, year]);

  const kpis = useMemo(() => {
    const total    = filteredRisks.length;
    const critical = filteredRisks.filter((r) => r.riskResult === "CRITICO").length;
    const high     = filteredRisks.filter((r) => r.riskResult === "ALTO").length;
    const moderate = filteredRisks.filter((r) => r.riskResult === "MODERADO").length;
    const low      = filteredRisks.filter((r) => r.riskResult === "BAJO").length;
    const highRate = total > 0 ? Math.round(((critical + high) / total) * 100) : 0;
    const residualCritical = filteredRisks.filter((r) => r.residualRisk === "CRITICO").length;
    const reduced = filteredRisks.filter((r) => {
      const levels = ["CRITICO", "ALTO", "MODERADO", "BAJO"];
      const i = levels.indexOf(r.riskResult);
      const j = levels.indexOf(r.residualRisk);
      return i !== -1 && j > i;
    }).length;
    return { total, critical, high, moderate, low, highRate, residualCritical, reduced };
  }, [filteredRisks]);

  const riskLevelPie = useMemo(() => {
    const counts = {};
    filteredRisks.forEach((r) => { if (r.riskResult) counts[r.riskResult] = (counts[r.riskResult] || 0) + 1; });
    return RISK_ORDER.filter((l) => counts[l]).map((l) => ({ type: l, value: counts[l] }));
  }, [filteredRisks]);

  const residualPie = useMemo(() => {
    const counts = {};
    filteredRisks.forEach((r) => { if (r.residualRisk) counts[r.residualRisk] = (counts[r.residualRisk] || 0) + 1; });
    return RISK_ORDER.filter((l) => counts[l]).map((l) => ({ type: l, value: counts[l] }));
  }, [filteredRisks]);

  const hazardBar = useMemo(() => {
    const counts = {};
    filteredRisks.forEach((r) => { if (r.hazardSource) counts[r.hazardSource] = (counts[r.hazardSource] || 0) + 1; });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([k, v]) => ({ tipo: resolveHazardLabel(k), value: v }));
  }, [filteredRisks]);

  const riskTypeBar = useMemo(() => {
    const counts = {};
    filteredRisks.forEach((r) => { if (r.riskType) counts[r.riskType] = (counts[r.riskType] || 0) + 1; });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([k, v]) => ({ tipo: RISK_TYPE_LABELS[k] || k, value: v }));
  }, [filteredRisks]);

  const bySite = useMemo(() => {
    if (!sites.length) return [];
    return sites.map((s) => {
      const siteRisks = filteredRisks.filter((r) => {
        const sid = typeof r.site === "object" ? r.site?._id : r.site;
        return sid === s._id;
      });
      return { sitio: s.name, value: siteRisks.length };
    }).filter((d) => d.value > 0);
  }, [filteredRisks, sites]);

  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportingPdf(true);
    const chartCleanups = [];
    try {
      await document.fonts.ready;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const [riskLevelDonut, residualDonut, riskTypeColumn, hazardColumn, siteColumn] = await Promise.all([
        renderDonutForPdf(riskLevelPie, RISK_COLORS, { showPercentLabels: true }),
        renderDonutForPdf(residualPie, RISK_COLORS, { showPercentLabels: true }),
        renderColumnForPdf(riskTypeBar, { xField: "tipo", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25, xLabelFontSize: 7 }),
        renderColumnForPdf(hazardBar, { xField: "tipo", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25, xLabelFontSize: 7 }),
        bySite.length > 1 ? renderColumnForPdf(bySite, { xField: "sitio", palette: GREEN_PALETTE, radius: 5, maxBarWidth: 25, width: 780, height: 260 }) : Promise.resolve({ isEmpty: true }),
      ]);
      [riskLevelDonut, residualDonut, riskTypeColumn, hazardColumn, siteColumn].forEach((r) => { if (r.cleanup) chartCleanups.push(r.cleanup); });

      const kpiCanvas = await captureElementAtWidth(kpiRef.current, 1400, {}, prepareKpiCardsForPdf);

      const chartRows = [
        {
          height: 220,
          cards: [
            { title: t("risk_level_distribution") || "Distribución por nivel de riesgo inicial", ...riskLevelDonut, legendPosition: "right" },
            { title: t("residual_risk_distribution") || "Distribución por nivel de riesgo residual", ...residualDonut, legendPosition: "right" },
          ],
        },
        {
          height: 220,
          cards: [
            { title: t("risk_type_distribution") || "Evaluaciones por tipo de riesgo", ...riskTypeColumn },
            { title: t("hazard_source_distribution") || "Evaluaciones por fuente de peligro", ...hazardColumn },
          ],
        },
      ];
      if (bySite.length > 1) {
        chartRows.push({ height: 260, section: { title: t("risk_assessments_by_site") || "Evaluaciones por sitio", ...siteColumn } });
      }

      await buildStandardDashboardPdf({
        jsPDFCtor: jsPDF,
        pageTitle: `${t("risk_assessments") || "Evaluación de Riesgos"} — ${year}`,
        companyName: [companyName, siteName].filter(Boolean).join(" · "),
        generatedOnLabel: t("generated_on"),
        kpiCanvas,
        chartRows,
        filename: `evaluacion_riesgos_${year}.pdf`,
      });
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      chartCleanups.forEach((fn) => fn());
      exportingRef.current = false;
      setExportingPdf(false);
    }
  };

  if (risks === null) return <LoadingSpinner />;

  return (
    <div className="rad">
      {/* ── Filtros — afuera de la card gris de KPIs, arriba de todo (igual
          que los demás dashboards) ── */}
      <div className="rad__filters">
        <YearBar year={year} options={YEAR_OPTIONS} onChange={setYear} />
        <ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} title={t("export_pdf")} />
      </div>

      {/* KPIs */}
      <div className="resumen-wrapper rad__kpi-wrapper">
        <div className="rad__kpi-row" ref={kpiRef}>
          <KPICard
            label={t("total_evaluations") || "Total evaluaciones"}
            value={kpis.total}
          />
          <KPICard
            label={t("critical_risks") || "Críticos"}
            value={kpis.critical}
            subtitle={t("critical_risk_level") || "Nivel CRÍTICO"}
          />
          <KPICard
            label={t("high_risks") || "Altos"}
            value={kpis.high}
            subtitle="Nivel ALTO"
          />
          <KPICard
            label={t("high_risk_rate") || "Tasa crítico + alto"}
            value={`${kpis.highRate}%`}
            rawValue={kpis.highRate}
            showProgress
            progressColor="blue"
            subtitle={`${(kpis.critical || 0) + (kpis.high || 0)} / ${kpis.total}`}
          />
          <KPICard
            label={t("reduced_risks") || "Riesgo reducido"}
            value={kpis.reduced}
            subtitle={t("with_controls") || "Con controles"}
          />
        </div>
      </div>

      {/* Fila 1: Nivel inicial + Nivel residual — mismo patrón que los
          demás dashboards: chart-slot gris + ChartBox + ChartLegend (acá
          con los colores semánticos de riesgo, no la paleta genérica). */}
      <div className="rad__charts">
        <div className="rad__chart-slot">
          <ChartBox title={t("risk_level_distribution") || "Distribución por nivel de riesgo inicial"}>
            {riskLevelPie.length > 0 ? (
              <div className="rad__pie-row">
                <div className="chartWrapper rad__pie-wrapper">
                  <Pie {...makePie(riskLevelPie, (t) => RISK_COLORS[t] || "#9e9e9e")} />
                </div>
                <ChartLegend items={riskLevelPie.map((d) => ({ label: d.type, color: RISK_COLORS[d.type] || "#9e9e9e" }))} />
              </div>
            ) : (
              <div className="chartWrapper rad__pie-wrapper"><div className="rad__empty-ring" /></div>
            )}
          </ChartBox>
        </div>

        <div className="rad__chart-slot">
          <ChartBox title={t("residual_risk_distribution") || "Distribución por nivel de riesgo residual"}>
            {residualPie.length > 0 ? (
              <div className="rad__pie-row">
                <div className="chartWrapper rad__pie-wrapper">
                  <Pie {...makePie(residualPie, (t) => RISK_COLORS[t] || "#9e9e9e")} />
                </div>
                <ChartLegend items={residualPie.map((d) => ({ label: d.type, color: RISK_COLORS[d.type] || "#9e9e9e" }))} />
              </div>
            ) : (
              <div className="chartWrapper rad__pie-wrapper"><div className="rad__empty-ring" /></div>
            )}
          </ChartBox>
        </div>
      </div>

      {/* Fila 2: Tipo de riesgo + Fuente de peligro */}
      <div className="rad__charts">
        <div className="rad__chart-slot">
          <ChartBox title={t("risk_type_distribution") || "Evaluaciones por tipo de riesgo"}>
            <div className="chartWrapper">
              {riskTypeBar.length > 0 ? (
                <Column
                  data={riskTypeBar}
                  xField="tipo"
                  yField="value"
                  colorField="tipo"
                  scale={{ color: { range: GREEN_PALETTE }, x: BAR_SCALE_X }}
                  legend={false}
                  height={220}
                  style={BAR_STYLE}
                  axis={{ x: BAR_X_AXIS, y: BAR_Y_AXIS }}
                  tooltip={{ items: [{ channel: "y", name: "Cantidad" }] }}
                  interaction={BAR_INTERACTION}
                />
              ) : (
                <EmptyMsg />
              )}
            </div>
          </ChartBox>
        </div>

        <div className="rad__chart-slot">
          <ChartBox title={t("hazard_source_distribution") || "Evaluaciones por fuente de peligro"}>
            <div className="chartWrapper">
              {hazardBar.length > 0 ? (
                <Column
                  data={hazardBar}
                  xField="tipo"
                  yField="value"
                  colorField="tipo"
                  scale={{ color: { range: GREEN_PALETTE }, x: BAR_SCALE_X }}
                  legend={false}
                  height={220}
                  style={BAR_STYLE}
                  axis={{ x: BAR_X_AXIS, y: BAR_Y_AXIS }}
                  tooltip={{ items: [{ channel: "y", name: "Cantidad" }] }}
                  interaction={BAR_INTERACTION}
                />
              ) : (
                <EmptyMsg />
              )}
            </div>
          </ChartBox>
        </div>
      </div>

      {/* Fila 3: Por sitio (solo si hay más de uno) */}
      {bySite.length > 1 && (
        <div className="rad__charts">
          <div className="rad__chart-slot rad__chart-slot--wide">
            <ChartBox title={t("risk_assessments_by_site") || "Evaluaciones por sitio"}>
              <div className="chartWrapper">
                <Column
                  data={bySite}
                  xField="sitio"
                  yField="value"
                  colorField="sitio"
                  scale={{ color: { range: GREEN_PALETTE }, x: BAR_SCALE_X }}
                  legend={false}
                  height={220}
                  style={BAR_STYLE}
                  axis={{ x: BAR_X_AXIS, y: BAR_Y_AXIS }}
                  tooltip={{ items: [{ channel: "y", name: "Evaluaciones" }] }}
                  interaction={BAR_INTERACTION}
                />
              </div>
            </ChartBox>
          </div>
        </div>
      )}
    </div>
  );
}
